<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Incluir Hab/Div</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs" />
          </div>
          <div class="button-group">
            <v-btn color="primary" :loading="loading" @click="save">
              <v-icon left small>mdi-content-save-outline</v-icon>
              Enviar para análise
            </v-btn>
            <v-btn outlined color="primary" to="/divergencias">Voltar</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-alert text type="info">
      O pedido será identificado na Central de Análise como apresentado pela Recuperanda.
      Informe o credor e selecione a empresa devedora de cada crédito.
    </v-alert>

    <v-form ref="form" v-model="valid">
      <v-card flat outlined class="mb-5">
        <v-card-title>Credor</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="5">
              <v-text-field
                v-model="request.user_name"
                label="Nome ou razão social"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="request.user_registration"
                label="CPF/CNPJ"
                outlined
                dense
                :rules="[$rules.required, registrationRule]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="request.user_email"
                label="E-mail para notificações"
                type="email"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex align-center justify-space-between mb-3">
        <strong>Créditos apresentados pela Recuperanda</strong>
        <v-btn outlined color="primary" small @click="addItem">
          <v-icon left small>mdi-plus</v-icon>
          Adicionar crédito
        </v-btn>
      </div>

      <v-card
        v-for="(item, index) in request.items"
        :key="item.localId"
        flat
        outlined
        class="mb-4"
      >
        <v-card-title class="d-flex justify-space-between">
          <span>Crédito #{{ index + 1 }}</span>
          <v-btn
            icon
            color="error"
            :disabled="request.items.length === 1"
            @click="removeItem(index)"
          >
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="item.client_company_id"
                :items="companies"
                item-text="name"
                item-value="id"
                label="Empresa devedora"
                outlined
                dense
                :rules="[$rules.required]"
                @change="syncCompany(item)"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="item.origin"
                label="Contrato, título ou referência"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="item.type"
                :items="creditorTypes"
                item-text="name"
                item-value="id"
                label="Classe"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="item.currency"
                :items="currencyTypes"
                item-text="name"
                item-value="id"
                label="Moeda"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3">
              <v-currency-field
                v-model="item.amount"
                label="Valor apresentado"
                outlined
                dense
                :allowNegative="false"
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-file-input
                v-model="item.files"
                label="Documentos comprobatórios"
                multiple
                counter
                show-size
                outlined
                dense
                accept="image/png, image/jpeg, application/pdf, .csv, .xls, .xlsx"
                :rules="[filesRule, $rules.filesSize(20)]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-textarea
                v-model="item.observation"
                label="Observação"
                outlined
                dense
                rows="2"
                auto-grow
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import axios from "axios";
import { cpf, cnpj } from "cpf-cnpj-validator";
import currencyTypes from "@/common/data/currency-types.data";

export default {
  name: "DebtorEventCreate",

  data: () => ({
    loading: false,
    valid: true,
    currencyTypes,
    creditorTypes: [
      { id: 1, name: "Classe I - Trabalhista" },
      { id: 2, name: "Classe II - Garantia Real" },
      { id: 3, name: "Classe III - Quirografária" },
      { id: 4, name: "Classe IV - ME/EPP" },
      { id: 5, name: "Extraconcursal" },
    ],
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Habilitações/Divergências", disabled: false, to: "/divergencias", exact: true },
      { text: "Incluir Hab/Div", disabled: true },
    ],
    request: {
      user_name: "",
      user_registration: "",
      user_email: "",
      items: [],
    },
  }),

  computed: {
    selectedClient() {
      const user = this.$root.user || {};
      const clients = user.clients && user.clients.length
        ? user.clients
        : user.client
          ? [user.client]
          : [];
      const selectedClientId = Number(localStorage.getItem("selectedClientId") || user.client_id);
      return clients.find((client) => Number(client.id) === selectedClientId) || clients[0] || null;
    },

    companies() {
      if (!this.selectedClient) return [];
      const companies = Array.isArray(this.selectedClient.companies)
        ? this.selectedClient.companies
        : [];
      // Consolidacao substancial: QGC unico, o credito e do grupo (caso).
      if (Number(this.selectedClient.consolidation_type) === 2) return [{ id: "case", name: this.selectedClient.name }];
      return companies.length
        ? companies
        : [{ id: "case", name: this.selectedClient.name }];
    },
  },

  created() {
    this.addItem();
    this.loadCreditorClasses();
  },

  methods: {
    async loadCreditorClasses() {
      // A rota da devedora resolve o caso pelo token, entao nao e preciso
      // enviar o client_id daqui.
      try {
        const result = await axios.get("/api/admin/client/creditor-classes");
        const data = (result.data.content && result.data.content.data) || [];

        if (!data.length) return;

        this.creditorTypes = data.map((item) => ({
          id: item.legacy_class_id,
          name: item.name,
        }));
      } catch (error) {
        // Mantem as classes padrao ja definidas no componente.
      }
    },

    createItem() {
      const company = this.companies.length === 1 ? this.companies[0] : null;
      return {
        localId: `${Date.now()}-${Math.random()}`,
        client_company_id: company ? company.id : null,
        company_name: company ? company.name : "",
        origin: "",
        type: 3,
        currency: 1,
        amount: null,
        observation: "",
        files: [],
      };
    },

    addItem() {
      this.request.items.push(this.createItem());
    },

    removeItem(index) {
      this.request.items.splice(index, 1);
    },

    syncCompany(item) {
      const company = this.companies.find(
        (candidate) => String(candidate.id) === String(item.client_company_id)
      );
      item.company_name = company ? company.name : "";
    },

    registrationRule(value) {
      const registration = String(value || "").replace(/\D/g, "");
      return cpf.isValid(registration) || cnpj.isValid(registration) || "Informe um CPF/CNPJ válido";
    },

    filesRule(files) {
      return files && files.length ? true : "Adicione ao menos um documento";
    },

    buildPayload() {
      const payload = new FormData();
      const registration = String(this.request.user_registration || "").replace(/\D/g, "");

      payload.append("client_id", this.selectedClient ? this.selectedClient.id : "");
      payload.append("requests[0][user_name]", this.request.user_name);
      payload.append("requests[0][user_registration]", registration);
      payload.append("requests[0][user_email]", this.request.user_email || "");
      payload.append("requests[0][contact_name]", this.request.user_name);
      payload.append("requests[0][contact_email]", this.request.user_email || "");
      payload.append("requests[0][contact_registration]", registration);

      this.request.items.forEach((item, index) => {
        payload.append(`requests[0][items][${index}][origin]`, item.origin);
        payload.append(`requests[0][items][${index}][type]`, item.type);
        payload.append(`requests[0][items][${index}][currency]`, item.currency);
        payload.append(`requests[0][items][${index}][amount]`, item.amount);
        payload.append(`requests[0][items][${index}][company_name]`, item.company_name);
        if (item.client_company_id !== "case") {
          payload.append(`requests[0][items][${index}][client_company_id]`, item.client_company_id);
        }
        payload.append(`requests[0][items][${index}][observation]`, item.observation || "");
        item.files.forEach((file) => {
          payload.append(`requests[0][items][${index}][files]`, file, file.name);
        });
      });

      return payload;
    },

    async save() {
      if (!this.selectedClient) {
        this.$dialog.notify.error("Selecione um caso antes de incluir o pedido.", {
          position: "top-right",
          timeout: 4000,
        });
        return;
      }

      if (!this.$refs.form.validate()) return;

      const confirmed = await this.$dialog.confirm({
        title: "Enviar Hab/Div",
        text: "Deseja enviar estes créditos para análise conjunta da Administradora Judicial?",
        actions: { false: "Cancelar", true: "Enviar" },
      });
      if (!confirmed) return;

      this.loading = true;
      try {
        await axios.post("/api/admin/user-events/client/request", this.buildPayload());
        this.$dialog.notify.success("Pedido enviado para análise com sucesso.", {
          position: "top-right",
          timeout: 3500,
        });
        this.$router.replace("/divergencias");
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
